import { Tag, PageTagInfo } from './tag-extract-helpers';
import { ExtractTagsStep, TagsName } from './enums';
import { InfoTagDataItem } from './interfaces';
import { Rectangle } from '../interfaces';
import { Core } from '@pdftron/webviewer';


export class TagDataExtractor {
    private readonly tag: Tag = new Tag();

    constructor(private readonly pdfNetUtil: any,
        private readonly pdfDoc: Core.PDFNet.PDFDoc) { }

    getCommonData(): any {
        return {
            serialNumber: this.tag.serialNumber,
            type: this.tag.type,
            version: this.tag.version,
            adibib: this.tag.adibib,
            docLang: this.tag.docLang,
            tagEd: this.tag.tagEd
        };
    }

    async tagsProcess() {
        await this.processCommonData();

        for (const page of this.tag.pageInfo) {
            if (!page.isParsed && !page.isInProgress) {
                const timeStart = performance.now();
                await this.getPageData(page.id);
                console.log(`page ${page.id} tag ${this.tag.type} extraction time:`,
                    Math.round((performance.now() - timeStart) * 100) / 100000, 'page data:', page.parsedPage);
            }
        }
    }

    async getPageData(id: number): Promise<InfoTagDataItem | null> {
        if (this.tag.pageInfo.length === 0) {
            await this.processCommonData();
        }

        if (!this.pageExist(id)) {
            return null;
        }

        let page = this.tag.pageInfo.find(x => x.id === id);

        if (page && !page.parsedPage && !page.isInProgress) {
            page.isInProgress = true;
            page = await this.extractTagForPage(page);
            page.isInProgress = false;
        }

        if (page && page.parsedPage) {
            this.expandLanguageZones(page.parsedPage);
        }
        if (page) {
            return page.parsedPage;
        }
        return null;
    }

    private async processCommonData() {
        const root = await this.pdfDoc.getRoot();
        const tagName = await this.getPrioritedTagName(root);

        if (!tagName) {
            return Promise.resolve(null);
        }

        return new Promise(resolve => {
            this.extractTag(tagName, root).then(tagInfo => {
                if (tagInfo.hasOwnProperty('PageInfo')) {

                    this.tag.serialNumber = tagInfo.SN;
                    this.tag.tagEd = tagInfo.TagEd;
                    this.tag.type = tagInfo.Type;
                    this.tag.version = tagInfo.Version;

                    let pagesArr = [];

                    if (tagInfo.PageInfo.hasOwnProperty('Kids')) {
                        for (const item of tagInfo.PageInfo.Kids) {
                            if (item.hasOwnProperty('Nums')) {
                                pagesArr = pagesArr.concat(item.Nums);
                            }
                        }
                    } else if (tagInfo.PageInfo.hasOwnProperty('Nums')) {
                        pagesArr = tagInfo.PageInfo.Nums;
                    } else {
                        resolve(null);
                    }

                    let lastStoredPage = 0;

                    for (let i = 0; i < pagesArr.length; i++) {
                        const idx = pagesArr[i];
                        lastStoredPage = !isNaN(parseInt(idx, undefined)) ? idx : lastStoredPage + 1;

                        i++;

                        if (i <= pagesArr.length - 1) {
                            this.getTagPage(lastStoredPage).rawPage = pagesArr[i];
                        }
                    }
                }

                resolve(null);
            });
        });
    }

    private async extractTagForPage(page): Promise<PageTagInfo> {
        const parsedPage: any = {};

        await this.dictionaryToObjectParallel(page.rawPage, ExtractTagsStep.NumsData, parsedPage);

        page.parsedPage = parsedPage;
        page.isParsed = true;

        return Promise.resolve(page);
    }

    private async getPrioritedTagName(root): Promise<string | null> {
        let result: string | null = null;

        if (await root.findObj(TagsName.AsgardInfo)) {
            result = TagsName.AsgardInfo;
        } else if (await root.findObj(TagsName.SensotecInfo)) {
            result = TagsName.SensotecInfo;
        } else if (await root.findObj(TagsName.SprintInfo)) {
            result = TagsName.SprintInfo;
        } else if (await root.findObj(TagsName.AdibibInfo)) {
            result = TagsName.AdibibInfo;
        }

        return Promise.resolve(result);
    }

    private pageExist(id: number): boolean {
        return this.tag.pageInfo.some(x => x.id === id);
    }

    private async extractTag(tag, root) {
        this.pdfNetUtil.startDeallocateStack();
        const infoDict = await root.findObj(tag);

        const result: any = {};

        if (infoDict !== null) {
            await this.dictionaryToObjectParallel(infoDict, ExtractTagsStep.Nums, result);
        }

        this.pdfNetUtil.endDeallocateStack();

        return result;
    }

    private dictionaryToObjectParallel(dict, extracTagsStep: ExtractTagsStep | null = null, outObj = {}) {
        let rootIter: any = null;
        let dictSize: number | null = null;

        return new Promise(resolveObj =>
            Promise.all([
                dict.getDictIterator().then(x => rootIter = x),
                dict.size().then(x => dictSize = x),
            ]).then(() => {
                const objPromises: any[] = [];
                if (dictSize !== null) {
                    for (let i = 0; i < dictSize; i++) {
                        let key: any = null;
                        let value: any = null;
                        let keyName: any = null;
                        let valueType: any = null;

                        Promise.all([rootIter.key().then(a => key = a), rootIter.value().then(b => value = b)]).then(() =>
                            Promise.all([key.getName().then(c => keyName = c), value.getType().then(d => valueType = d)]).then(() => {

                                if (extracTagsStep === ExtractTagsStep.Nums && keyName === 'Nums') {
                                    extracTagsStep = ExtractTagsStep.NumsArray;
                                }

                                objPromises.push(this.getPrimitiveValueParallel(value, valueType, extracTagsStep)
                                    .then(x => this.assignExtractedValue(keyName, outObj, x)));

                                if (objPromises.length === dictSize) {
                                    Promise.all(objPromises).then(() => resolveObj(outObj));
                                }
                            })
                        );

                        rootIter.next();
                    }
                }

                rootIter.destroy();
            })
        );
    }

    private getPrimitiveValueParallel(value: any, type: number, extracTagsStep: ExtractTagsStep | null = null) {
        switch (type) {
            case 0:
                return Promise.resolve(null);
            case 1:
                return value.getBool();
            case 2:
                return value.getNumber();
            case 3:
                return value.getName();
            case 4:
                return value.getAsPDFText();
            case 5:
                if (extracTagsStep !== ExtractTagsStep.NumsArray) {
                    return this.dictionaryToObjectParallel(value, extracTagsStep);
                } else {
                    return Promise.resolve(value);
                }
            case 6:
                return this.getArrayValueParallel(value, extracTagsStep);
            default:
                return `type ${type} not supported yet`;
        }
    }

    private getArrayValueParallel(value: any, extracTagsStep: ExtractTagsStep | null = null) {
        const result: any[] = [];
        const arrPromises: any[] = [];
        let size = 0;

        return new Promise(resolveArr => {
            value.size().then(x => {
                size = x;

                if (size === 0) {
                    resolveArr(result);
                }

                for (let i = 0; i < size; i++) {
                    value.getAt(i).then(obj => {
                        obj.getType().then(type => {
                            arrPromises.push(
                                this.getPrimitiveValueParallel(obj, type, extracTagsStep)
                                    .then(res => result.push({ arrPos: i, arrItem: res }))
                            );

                            if (arrPromises.length === size) {
                                Promise.all(arrPromises).then(() => {
                                    const sortedRes = result.sort((a, b) => {
                                        return a.arrPos - b.arrPos;
                                    });
                                    resolveArr(sortedRes.map(item => item.arrItem));
                                }
                                );
                            }
                        });
                    });
                }
            });
        });
    }

    private assignExtractedValue(keyName: string, outObj: any, value: any) {
        if (keyName === 'BBox' && value.length === 4) {
            const rect: Rectangle = { x: value[0], y: value[1], width: value[2] - value[0], height: value[3] - value[1] };
            outObj[keyName] = rect;
        } else {
            outObj[keyName] = value;
        }
    }

    private getTagPage(id) {
        const filtered = this.tag.pageInfo.filter(x => x.id === id);

        if (filtered.length > 0) {
            return filtered[0];
        } else {
            const newTagPage = { id, rawPage: null, parsedPage: null, isParsed: false, isInProgress: false };
            this.tag.pageInfo.push(newTagPage);
            return newTagPage;
        }
    }

    private expandLanguageZones(zones: InfoTagDataItem) {
        if (zones.Language) {
            zones.Language = zones.Language.concat(this.getLanguageZonesFromOrderZones(zones.Correct));
        }
    }

    private getLanguageZonesFromOrderZones(orderZones: { BBox: Rectangle, Language: number }[]) {
        const result: any[] = [];

        orderZones.forEach(zone => {
            if (zone.Language > 0) {
                result.push({ LangID: Number(zone.Language), BBox: zone.BBox });
            }
        });

        return result;
    }
}
